import { useState, useRef, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { gsap } from 'gsap';
import { FiArrowRight } from 'react-icons/fi';
import { FaReact, FaNodeJs, FaDatabase, FaJs, FaPython } from 'react-icons/fa';
import Union from "../../../assets/Union.png";
import { createPortal } from 'react-dom'
import './TechOrb.scss';

const TechOrb = () => {
    const orbRef = useRef();
    const eyeRef = useRef();
    const ringRef = useRef();
    const { scrollY } = useScroll();
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [isHero, setIsHero] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const [hasScrolledPast100, setHasScrolledPast100] = useState(false);

    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkIfMobile();
        window.addEventListener('resize', checkIfMobile);
        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    useEffect(() => {
        const handleScroll = (y) => {
            const scrolledPast100 = y > 100;
            if (scrolledPast100 !== hasScrolledPast100) {
                setHasScrolledPast100(scrolledPast100);
            }
            setIsHero(y < 100);
        };

        const unsubscribe = scrollY.on("change", handleScroll);

        return () => unsubscribe();
    }, [hasScrolledPast100, scrollY]);


    useEffect(() => {
        if (isMobile) return;

        const handleMouseMove = (e) => {
            const { clientX, clientY } = e;
            const { left, top, width, height } = orbRef.current?.getBoundingClientRect() || {};
            if (!left && !top) return;

            const centerX = left + width / 2;
            const centerY = top + height / 2;

            setMousePosition({
                x: clientX - centerX,
                y: clientY - centerY
            });
        };

        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, [isMobile]);

    useEffect(() => {
        if (!ringRef.current) return;

        const icons = ringRef.current.querySelectorAll('.tech-icon');
        const radius = isMobile ? 70 : 90;
        const speed = 0.01;

        let angle = 0;
        const animateIcons = () => {
            angle += speed;

            icons.forEach((icon, i) => {
                const offsetAngle = angle + (i * (Math.PI * 2 / icons.length));
                const x = Math.cos(offsetAngle) * radius;
                const y = Math.sin(offsetAngle) * radius;

                gsap.to(icon, {
                    x,
                    y,
                    rotation: 0,
                    duration: 0.1,
                    ease: 'none'
                });
            });

            requestAnimationFrame(animateIcons);
        };

        animateIcons();
    }, [isMobile]);

    const finalPosition = {
        x: window.innerWidth - 160,
        y: window.innerHeight - 160,
        scale: 0.35,
        opacity: 0.9
    };

    const initialPosition = {
        x: (window.innerWidth / 2) - 90,
        y: window.innerWidth <= 768 ? 130 : window.innerWidth <= 1200 ? 150 : 160,
        scale: 1,
        opacity: 1
    };

    const eyeX = useTransform(() => {
        if (isMobile) return 0;
        const maxMovement = 10;
        return Math.min(Math.max(mousePosition.x / 30, -maxMovement), maxMovement);
    });

    const eyeY = useTransform(() => {
        if (isMobile) return 0;
        const maxMovement = 10;
        return Math.min(Math.max(mousePosition.y / 30, -maxMovement), maxMovement);
    });

    if (isMobile) {
        return (
            <div className="tech-orb-mobile" ref={orbRef}>
                <div className="tech-ring-path"></div>
                <div ref={ringRef} className="tech-icons-container">
                    {[FaReact, FaNodeJs, FaDatabase, FaJs, FaPython].map((Icon, index) => (
                        <div key={index} className="tech-icon">
                            <Icon size={20} />
                        </div>
                    ))}
                </div>
                <div className="orb-main">
                    <div className="orb-eye">
                        <img src={Union} alt="Union logo" />
                    </div>
                </div>
            </div>
        );
    }

    const orbContent = (
        <motion.div
            ref={orbRef}
            className="tech-orb"
            initial={{ scale: 0 }}
            animate={{
                ...(hasScrolledPast100 ? finalPosition : initialPosition),
                transition: {
                    type: 'spring',
                    stiffness: 200,
                    damping: 20,
                    mass: 1.5
                }
            }}
            whileHover={{ scale: hasScrolledPast100 ? 0.4 : 1.05 }}
        >
            <div className="tech-ring-path"></div>

            <div ref={ringRef} className="tech-icons-container">
                {[FaReact, FaNodeJs, FaDatabase, FaJs, FaPython].map((Icon, index) => (
                    <div key={index} className="tech-icon">
                        <Icon size={20} />
                    </div>
                ))}
            </div>

            <div className="orb-main">
                <motion.div
                    ref={eyeRef}
                    className="orb-eye"
                    style={{
                        x: eyeX,
                        y: eyeY
                    }}
                    transition={{
                        type: 'spring',
                        stiffness: 100,
                        damping: 10
                    }}
                >
                    <img src={Union} alt="Union logo" />
                </motion.div>
            </div>


        </motion.div>
    );
    // Render orb into document.body so it's outside page stacking contexts
    if (typeof document !== 'undefined') {
        return createPortal(orbContent, document.body)
    }

    // SSR-safe fallback
    return null
};

export default TechOrb;